import { ref, shallowRef, computed, watch, watchEffect } from 'vue';
import { useCached, provideLocal, injectLocal } from '@vueuse/core';
import { useUpc } from '@nuxt-common/media-card-large/app/composables/useUpc';
import { useLoyalty } from '~/composables/loyalty/useLoyalty';
import { post } from '~/utils/api';
import { useEnvProps } from '~/composables/useEnvProps';
import { isEmpty, formatPoints } from '~/utils/commonUtils';
import { PdpUserStatus } from '~/utils/pdpUserStatus';
import { useQuantity } from '~/modules/product/composables/useQuantity';
import { useProduct } from '~/modules/product/composables/useProduct';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import { useUser } from '~/composables/user/useUser';
import { getLowestPrice } from '~/modules/price/parsers';
import { useProductPrice } from '~/modules/price/composables/useProductPrice';
import useSelectedMemberId from '~/modules/latchkey/composables/useSelectedMemberId';
import { LOYALTY_HEADERS, REGEX_TIERS_NAMES } from 'config';
import { LOYALTY_3PC_OFFER_NAMES, LOYALTY_TIER_DETAILS, PPC_DATA } from '~/modules/ppc/constants/ppc';

import { ppcMapper } from '../mappers';
import { formatFloatingPoints, isValidPayload as isValidPayloadUtil } from '../utils';
// @returns (mcom) -> /BRONZE|SILVER|GOLD|PLATINUM/gi
// @returns (bcom) -> /BASE|TOL|UNL/gi
const TIERS_REGEX_EXPRESSION = new RegExp(REGEX_TIERS_NAMES.join('|'), 'gi');

const LOYALTY_3PC_OFFER_NAMES_ARR = Object.values(LOYALTY_3PC_OFFER_NAMES);

/**
 * @returns {import('../types').UsePpcResponse}
 */
export function usePpc() {
  const ppcData = injectLocal(PPC_DATA);
  if (typeof ppcData === 'undefined') {
    throw new TypeError('usePpc() is called without provider.');
  }

  return ppcData;
}

export function usePpcProvide() {
  const {
    killswitches: { rewardBadgeForDigitalEnabled = false },
  } = useKillswitches();
  const { isPpcEnabled, isShopTheSetExpEnabled } = useFeatureEligibility();
  const { hostlink, isBcom } = useEnvProps();
  const PPC_URL = `${hostlink}/xapi/loyalty/v1/prepurchasepoints?_origin=pdp`;

  const cachedPrice = useCached(ref(1));
  const cachedQuantity = useCached(ref(1));

  const { quantity } = useQuantity();
  const { productId } = useProduct();
  const selectedMemberId = useSelectedMemberId();
  const { userGuid, userId, bagGuid } = useUser();
  const { tierName, encryptedLoyaltyId, defaultTenderType, isUserNonLoyalist } = useLoyalty();
  const { upcData, selectedUpcId } = useUpc(isShopTheSetExpEnabled ? selectedMemberId : productId);
  const selectedPrice = useProductPrice();

  const isShowPpc = ref(false);
  const ppcProps = shallowRef({});
  const modalProps = shallowRef({});
  const ppcIconName = ref('');
  const isPpcAvailable = ref(false);
  const offerType = ref('base');
  const rewardBadgeEnabled = ref(false);
  const ppcLoading = ref(null);

  const isPpcLoading = computed({
    get() {
      return ppcLoading.value !== null
        ? ppcLoading.value
        : !isUserNonLoyalist.value && isPpcEnabled && selectedPrice.value?.offersData?.topOffer?.isLoyallistOffer;
    },
    set(value) {
      ppcLoading.value = value;
    },
  });

  const isBCCDefault = computed(() => isBcom && defaultTenderType.value === 'U');
  const isLoyaltyPromoPresent = computed(
    () => isBcom && Boolean(selectedPrice.value?.offersData?.offerList?.find((offer) => offer?.isLoyallistOffer)),
  );
  const isBaseOffer = computed(() => offerType.value === 'base');
  const isPowerPointsOffer = computed(() => offerType.value === 'powerPoints');
  const isMultiplierOffer = computed(() => offerType.value === 'multiplier');
  const isPointsPerDollarOffer = computed(() => offerType.value === 'pointsPerDollar');

  const isMultiplierOrPointsPerDollar = computed(() => isMultiplierOffer.value || isPointsPerDollarOffer.value);

  const isPowerPointsBelowPointsThreshold = computed(() => isBcom && isPowerPointsOffer.value && !rewardBadgeEnabled.value);

  function _setPpcProps(newProps = {}) {
    const prevProps = ppcProps.value;
    ppcProps.value = {
      ...prevProps,
      ...newProps,
      rewardBadgeEnabled: rewardBadgeEnabled.value,
    };
  }

  function _setModalProps(newProps = {}) {
    const prevProps = modalProps.value;
    modalProps.value = {
      ...prevProps,
      ...newProps,
      ...(isBcom
        ? { isPowerPointsOffer: isPowerPointsOffer.value, isBccDefault: isBCCDefault.value }
        : { rewardBadgeEnabled: rewardBadgeEnabled.value }),
    };
  }

  function _getPpcAvailability(parsedPoints) {
    const parsedPointsDefined = parsedPoints > 0;

    if (isBcom) {
      return parsedPointsDefined && PdpUserStatus.loyaltyCustomerSignInValidation();
    }

    return parsedPointsDefined;
  }

  function _isBonusDayEligible() {
    const { offers } = selectedPrice.value;
    const activeOffers = offers?.lists?.activeOffers || /* istanbul ignore next */ [];
    const result = activeOffers.filter((element) => element.header === 'Earn Bonus Points NOW');
    return result.length !== 0;
  }

  /**
   *
   * @param {obj[]} [items=[]]
   * @returns {obj}
   * @private
   */
  function _getFirstOfferDetails(items = []) {
    // take the first offer only
    const offersArr = items[0]?.offerDetails ?? [];
    // take first offer of filtered offer types (should only be: MULTIPLIER, PULSE, or FIXED) or empty object
    return offersArr.filter((offer) => LOYALTY_3PC_OFFER_NAMES_ARR.includes(offer.offerType))[0] ?? {};
  }

  /**
   *
   * @param {string} _offerType
   * @returns {string}
   * @private
   */
  function _getLoyaltyOffer(_offerType) {
    switch (_offerType) {
      case LOYALTY_3PC_OFFER_NAMES.multiplier:
        return 'multiplier';
      case LOYALTY_3PC_OFFER_NAMES.pointsPerDollar:
        return 'pointsPerDollar';
      case LOYALTY_3PC_OFFER_NAMES.powerPoints:
        return 'powerPoints';
      default:
        return 'base';
    }
  }

  /**
   *
   * @param {string} _offerType
   * @param {obj} offerData
   * @returns {{ multiplier?: string, pointsPerDollar?: string }}
   * @private
   */
  function _getOfferProps(_offerType, offerData) {
    switch (_offerType) {
      case 'multiplier':
        return { multiplier: formatFloatingPoints(offerData.multiplier ?? 0) };
      case 'pointsPerDollar':
        return { pointsPerDollar: formatFloatingPoints(offerData.points ?? 0) };
      default:
        return {};
    }
  }

  /**
   * returns offer related ppc props
   * @param {obj[]} items
   * @returns {{offerType: string, pointsPerDollar?: string, multiplier?: string}}
   * @private
   */
  function _getLoyaltyOfferProps(items) {
    const offerData = _getFirstOfferDetails(items);
    const _offerType = _getLoyaltyOffer(offerData?.offerType ?? '');
    const props = _getOfferProps(_offerType, offerData);
    return {
      offerType: _offerType,
      ...props,
    };
  }

  function _isRewardBadgeEnabled(rewardAmount, isBrandExcluded = false) {
    return rewardBadgeForDigitalEnabled && rewardAmount > 0 && (isBcom ? !isBrandExcluded : PdpUserStatus.loyaltyCustomerSignInValidation());
  }

  /**
   * @param {import('../types').Response} response
   * @param {boolean} [forcedAvailable=false]
   * @returns {void}
   * */
  function _updatePpcData({ points }, forcedAvailable = false) {
    const parsedPendingPoints = parseInt(points.totalPendingPoints, 10);

    isPpcAvailable.value = forcedAvailable || _getPpcAvailability(parsedPendingPoints);

    const pointsPerItems = points.items;
    // get dynamic props
    const loyaltyProps = {
      pendingPoints: formatPoints(parsedPendingPoints),
      rewardAmount: parseInt(points.rewardAmount ?? '0.0', 10),
      ...(isBcom
        ? _getLoyaltyOfferProps(pointsPerItems)
        : {
            isBonusDayBadge: _isBonusDayEligible(),
            isBonusPointsBadge: +points.bonusPoints > 0,
          }),
    };

    // validate member is eligible for star money (rewards) badge
    rewardBadgeEnabled.value = _isRewardBadgeEnabled(loyaltyProps.rewardAmount, pointsPerItems?.[0]?.isBrandExcluded);
    // modify ppc props
    if (isBcom) {
      offerType.value = loyaltyProps.offerType;
      // add loyaltyProp fields
      loyaltyProps.isBaseOffer = isBaseOffer.value;
      loyaltyProps.isPowerPointsOffer = isPowerPointsOffer.value;
      loyaltyProps.isMultiplierOffer = isMultiplierOffer.value;
      loyaltyProps.isPointsPerDollarOffer = isPointsPerDollarOffer.value;
      loyaltyProps.isBccDefault = isBCCDefault.value;
    }

    // update ppc props
    _setPpcProps(loyaltyProps);
    // update modal props
    _setModalProps();
  }

  function _getLoyaltyTierData() {
    const tierMatches = tierName.value.match(TIERS_REGEX_EXPRESSION) ?? [];
    const [tier = ''] = tierMatches;

    const formattedTier = (tier.length ? tier : tierName.value).toLowerCase();

    return LOYALTY_TIER_DETAILS[formattedTier] ?? {};
  }

  function _findLoyaltyTiersDetails() {
    if (isUserNonLoyalist.value) return;

    const { icon = '', membershipDetails = {} } = _getLoyaltyTierData();

    const applyMembershipDetailsProps = isBcom && 'membership' in membershipDetails;

    ppcIconName.value = icon;
    _setModalProps();
    if (isBcom && applyMembershipDetailsProps) {
      // update ppc props member details based on loyalty tier change
      _setPpcProps({ membershipDetails });
    }
  }

  function _changeCachedQuantity() {
    cachedQuantity.value = quantity.value;
  }

  function _changeCachedPrice() {
    cachedPrice.value = getLowestPrice(selectedPrice.value);
  }

  /**
   * @param {import('../types').Response} mockResponse
   * @param {boolean} [forcedAvailable=false]
   * @returns {Promise<void>}
   * */
  async function fetchPpcData({ mockResponse = null, forcedAvailable = false } = {}) {
    if (isUserNonLoyalist.value) return;
    if (mockResponse) {
      isPpcLoading.value = false;
      _updatePpcData(mockResponse, forcedAvailable);
      return;
    }

    const { requiredPayload, fullPayload } = ppcMapper({
      upcData: upcData.value,
      selectedUpcId: selectedUpcId.value,
      selectedPrice: selectedPrice.value,
      quantity: quantity.value,
      userGUID: userGuid.value,
      userId: userId.value,
      bagGuid: bagGuid.value,
      tier: tierName.value.toUpperCase(),
      encryptedLoyaltyId: encryptedLoyaltyId.value,
      tenderType: defaultTenderType.value,
    });
    const isValidPayload = isValidPayloadUtil(requiredPayload);

    if (isValidPayload && quantity.value >= 1) {
      try {
        const response = await post(PPC_URL, fullPayload, { headers: LOYALTY_HEADERS });

        if (isEmpty(response)) throw new Error('PPC response is empty');

        _updatePpcData(response);
      } catch (e) {
        console.error(`PPC request failed: ${e}`);
      } finally {
        isPpcLoading.value = false;
      }
    } else {
      isPpcLoading.value = false;
      console.error(`PPC payload is incorrect or quantity < 1: payload: ${fullPayload}, quantity: ${quantity.value}`);
    }
  }

  watch(quantity, _changeCachedQuantity, { immediate: true });
  watch(selectedPrice, _changeCachedPrice, { deep: true, immediate: true });
  // updates props with appropriate tier specific data
  watch(tierName, _findLoyaltyTiersDetails, { immediate: !isUserNonLoyalist.value });

  if (isPpcEnabled) {
    // todos: only one watcher by tier name
    watch([cachedQuantity, cachedPrice, tierName], fetchPpcData);
  }

  watchEffect(() => {
    isShowPpc.value = isPpcEnabled && isPpcAvailable.value && !isUserNonLoyalist.value && !(isPointsPerDollarOffer.value && isBCCDefault.value);
  });

  provideLocal(PPC_DATA, {
    fetchPpcData,
    ppcProps,
    modalProps,
    ppcIconName,
    isBaseOffer,
    isPowerPointsOffer,
    isMultiplierOrPointsPerDollar,
    isShowPpc,
    isPpcLoading,
    rewardBadgeEnabled,
    isPowerPointsBelowPointsThreshold,
    isLoyaltyPromoPresent,
    isBCCDefault,
  });
}
